import React, { useEffect, useRef, useState } from "react";
import {
  BellOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { Badge, Button, Dropdown, List, Modal, Avatar, Tag, Tabs } from "antd";
import { ContentSyncJob, ContentSyncLog } from "interfaces";
import dayjs from "dayjs";

type SyncJobStatus = "queued" | "in_progress" | "completed" | "failed";

interface SyncNotificationButtonProps {
  jobs: ContentSyncJob[];
}

const getStatusIcon = (status: SyncJobStatus) => {
  switch (status) {
    case "queued":
      return <ClockCircleOutlined style={{ color: "#faad14" }} />;
    case "in_progress":
      return <SyncOutlined spin style={{ color: "#1890ff" }} />;
    case "completed":
      return <CheckCircleOutlined style={{ color: "#52c41a" }} />;
    case "failed":
      return <CloseCircleOutlined style={{ color: "#ff4d4f" }} />;
  }
};

export const SyncNotificationButton: React.FC<SyncNotificationButtonProps> = ({
  jobs,
}) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedJob, setSelectedJob] = useState<ContentSyncJob | null>(null);
  const [activeTab, setActiveTab] = useState("database");
  const [lastSeenLogs, setLastSeenLogs] = useState<{
    database: number;
    canvas: number;
  }>({
    database: 0,
    canvas: 0,
  });

  const logsContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!selectedJob) return;

    const updated = jobs.find((job) => job.id === selectedJob.id);
    if (updated) {
      setSelectedJob(updated);

      const databaseLogs =
        updated.logs?.filter((log) => log.log_type === "DATABASE") || [];
      const canvasLogs =
        updated.logs?.filter((log) => log.log_type === "CANVAS") || [];
      setLastSeenLogs({
        database:
          activeTab === "database"
            ? databaseLogs.length
            : lastSeenLogs.database,
        canvas:
          activeTab === "canvas" ? canvasLogs.length : lastSeenLogs.canvas,
      });
    }
  }, [jobs]);

  useEffect(() => {
    if (logsContainerRef.current) {
      logsContainerRef.current.scrollTo({
        top: logsContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [activeTab, selectedJob]);

  const handleJobClick = (job: ContentSyncJob) => {
    const databaseLogs =
      job.logs?.filter((log) => log.log_type === "DATABASE") || [];
    const canvasLogs =
      job.logs?.filter((log) => log.log_type === "CANVAS") || [];

    setSelectedJob(job);
    setModalVisible(true);
    setActiveTab("database");

    // Reset seen logs to 0 when a new job is opened
    setLastSeenLogs({
      database: databaseLogs.length,
      canvas: canvasLogs.length,
    });
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key);

    const logCount =
      selectedJob?.logs?.filter((log) => log.log_type === key.toUpperCase())
        .length || 0;

    setLastSeenLogs((prev) => ({
      ...prev,
      [key]: logCount,
    }));
  };
  const renderLogs = (logs: ContentSyncLog[] = []) => (
    <List
  size="small"
  dataSource={logs}
  renderItem={(log, index) => (
    <List.Item key={index}>
      <div style={{ display: "flex", width: "100%" }}>
        <div style={{ minWidth: 60 }}>
          <Tag
            color={
              log.level === "INFO"
                ? "blue"
                : log.level === "WARNING"
                ? "orange"
                : log.level === "ERROR"
                ? "red"
                : "default"
            }
          >
            {log.level.toUpperCase()}
          </Tag>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, color: "#888" }}>
            {dayjs(log.timestamp).format("HH:mm")}
          </div>
          <div style={{ marginTop: 0 }}>
            {log.message.map((part, i) =>
              typeof part === "string" ? (
                <span key={i}>{part}</span>
              ) : part.type === "link" ? (
                <a key={i} href={part.href} target="_blank" rel="noopener noreferrer">
                  {part.text}
                </a>
              ) : null
            )}
          </div>
        </div>
      </div>
    </List.Item>
  )}
/>

  );

  const menu = (
    <div
      style={{
        maxHeight: 600,
        overflowY: "auto",
        width: 300,
        padding: 8,
        background: "white",
        borderRadius: 8,
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
      }}
    >
      <List
        itemLayout="horizontal"
        dataSource={jobs}
        renderItem={(job) => (
          <List.Item
            onClick={() => handleJobClick(job)}
            style={{ cursor: "pointer" }}
          >
            <List.Item.Meta
              avatar={
                <Avatar
                  icon={getStatusIcon(job.status)}
                  style={{ backgroundColor: "transparent" }}
                />
              }
              title={job.title !== "" ? job.title : "Unnamed sync"}
              description={dayjs(job.created_at).format("HH:mm D MMM YYYY")}
            />
          </List.Item>
        )}
      />
    </div>
  );

  const databaseLogs =
    selectedJob?.logs?.filter((log) => log.log_type === "DATABASE") || [];

  const canvasLogs =
    selectedJob?.logs?.filter((log) => log.log_type === "CANVAS") || [];

  const newDatabaseLogs = databaseLogs.length - lastSeenLogs.database;
  const newCanvasLogs = canvasLogs.length - lastSeenLogs.canvas;

  return (
    <>
      <Dropdown overlay={menu} trigger={["click"]} placement="bottomRight">
        <Badge count={jobs.length} size="small">
          <Button shape="circle" icon={<SyncOutlined />} />
        </Badge>
      </Dropdown>

      <Modal
        title={
          <span style={{ display: "flex", alignItems: "center", gap: 0 }}>
            <Avatar
              icon={getStatusIcon(selectedJob?.status)}
              style={{ backgroundColor: "transparent" }}
            />
            {selectedJob?.title || "Unnamed sync"}
          </span>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
      >
        <Tabs activeKey={activeTab} onChange={handleTabChange}>
          <Tabs.TabPane
            key="database"
            tab={
              <span>
                <Badge
                  size="small"
                  count={
                    activeTab !== "database" && newDatabaseLogs > 0
                      ? newDatabaseLogs
                      : 0
                  }
                  offset={[6, 0]}
                  style={{
                    backgroundColor: "#f5222d",
                    fontSize: 10,
                    lineHeight: "14px",
                    height: 16,
                    minWidth: 16,
                  }}
                >
                  Database Logs
                </Badge>
              </span>
            }
          />

          <Tabs.TabPane
            key="canvas"
            tab={
              <span>
                <Badge
                  count={
                    activeTab !== "canvas" && newCanvasLogs > 0
                      ? newCanvasLogs
                      : 0
                  }
                  offset={[6, 0]}
                  style={{ backgroundColor: "#f5222d" }}
                >
                  Canvas Logs
                </Badge>
              </span>
            }
          />
        </Tabs>
        <div
          ref={logsContainerRef}
          style={{ maxHeight: 300, overflowY: "auto", marginTop: 8 }}
        >
          {renderLogs(activeTab === "database" ? databaseLogs : canvasLogs)}
        </div>
      </Modal>
    </>
  );
};
